import { sendEmailToChangeEmailApi, useMe } from "@/api/services/main/auth";
import { Form } from "@/components/elements/Form";
import { LoadingArea } from "@/components/elements/Loading/Loading";
import { ERROR_STATUS_CODES } from "@/constants/error";
import { INVALID_ALREADY_EXISTS } from "@/constants/invalids";
import { EmailChangeFields } from "@/features/user-setting/components/change-email/EmailChangeFields";
import { EmailChangeFormFooter } from "@/features/user-setting/components/change-email/EmailChangeFormFooter";
import { EmailChangeReview } from "@/features/user-setting/components/change-email/EmailChangeReview";
import { EmailChangeReviewFooter } from "@/features/user-setting/components/change-email/EmailChangeReviewFooter";
import { EmailSentModal } from "@/features/user-setting/components/change-email/EmailSentModal";
import { buildChangeEmailFormSchema } from "@/features/user-setting/schema/changeEmailFormSchema";
import { TChangeEmailForm } from "@/features/user-setting/types/changeEmailForm";
import { AppRoutes } from "@/routes/config";
import { text } from "@/theme/colors";
import { layout } from "@/theme/variables";
import { isApiError, setErrorForm, showError } from "@/utils/error";
import { replaceStr } from "@/utils/string";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Stack, Typography } from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import { Resolver, useForm } from "react-hook-form";
import { Navigate } from "react-router-dom";

const RESEND_COOLDOWN_SECONDS = 30;

type TStep = "form" | "review";

const SUBTEXT: Record<TStep, string> = {
  form: "ログインに使用するメールアドレスを変更します。確認後の認証が完了するまで変更は確定されません。",
  review: "メールアドレスを変更します。変更にはメールによる確認が必要なため、確認メールの送付後は受信したメールからURLを開いて変更を完了させてください。",
};

export const UserSettingChangeEmailPage: FC = () => {
  const { data, isLoading } = useMe();
  const me = data?.data;
  const currentEmail = me?.email;

  const [step, setStep] = useState<TStep>("form");
  const [newEmail, setNewEmail] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [hasSent, setHasSent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [modal, setModal] = useState<{ open: boolean; resent: boolean }>({ open: false, resent: false });

  const schema = useMemo(() => buildChangeEmailFormSchema(currentEmail ?? ""), [currentEmail]);

  const methods = useForm<TChangeEmailForm>({
    mode: "onChange",
    resolver: yupResolver(schema) as Resolver<TChangeEmailForm>,
    defaultValues: { newEmail: "" },
  });

  // Tick down the resend cooldown once a send has happened.
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((prev) => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const onSubmit = (form: TChangeEmailForm) => {
    setNewEmail(form.newEmail);
    setStep("review");
  };

  // Returning to the form may change the email, so the next send counts as a first send again.
  const handleBackToForm = () => {
    setStep("form");
    setHasSent(false);
    setResendCooldown(0);
  };

  const sendVerificationEmail = async (resent: boolean) => {
    setIsSending(true);
    try {
      await sendEmailToChangeEmailApi({ email: newEmail });
      setHasSent(true);
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      setModal({ open: true, resent });
    } catch (error) {
      if (isApiError(error) && error.code === ERROR_STATUS_CODES.EMAIL_HAS_BEEN_ALREADY_EXISTS) {
        methods.setError("newEmail", { message: replaceStr(INVALID_ALREADY_EXISTS, { path: "メールアドレス" }) });
        setStep("form");
      } else {
        showError(error);
      }
      setErrorForm(methods, error);
    } finally {
      setIsSending(false);
    }
  };

  if (isLoading) return <LoadingArea height={400} />;
  if (!me) return <Navigate to={AppRoutes.pageNotFound} replace />;

  return (
    <Form methods={methods} onSubmit={onSubmit} schema={schema}>
      <Stack gap="24px" minHeight="calc(100vh - 105px)">
        <Stack width="100%" alignItems="center">
          <Stack width={layout.pageContentWidth} maxWidth="100%" px="24px" gap="4px">
            <Typography variant="head20Bold" color={text.primary}>
              メールアドレス変更
            </Typography>
            <Typography variant="body14" color={text.secondary}>
              {SUBTEXT[step]}
            </Typography>
          </Stack>
        </Stack>

        <Stack flex={1} width="100%" alignItems="center">
          <Box width={layout.pageContentWidth} maxWidth="100%" px="24px">
            {step === "form" ? (
              <EmailChangeFields control={methods.control} currentEmail={currentEmail} />
            ) : (
              <EmailChangeReview currentEmail={currentEmail} newEmail={newEmail} />
            )}
          </Box>
        </Stack>

        <Stack width="100%" alignItems="center" sx={{ position: "sticky", bottom: 0, zIndex: 1 }}>
          <Box width={layout.pageContentWidth} maxWidth="100%" px="24px">
            {step === "form" ? (
              <EmailChangeFormFooter />
            ) : (
              <EmailChangeReviewFooter
                onBack={handleBackToForm}
                onSend={() => sendVerificationEmail(false)}
                onResend={() => sendVerificationEmail(true)}
                isSending={isSending}
                hasSent={hasSent}
                resendCooldown={resendCooldown}
              />
            )}
          </Box>
        </Stack>
      </Stack>

      <EmailSentModal isOpen={modal.open} resent={modal.resent} onClose={() => setModal((prev) => ({ ...prev, open: false }))} />
    </Form>
  );
};
